import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import formationService from '../../services/formationService';
import sessionService from '../../services/sessionService';
import userService from '../../services/userService';
import SearchableFormationSelect from '../../components/SearchableFormationSelect/SearchableFormationSelect';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { formatMoney } from '../../utils/money';
import { calculateConsommableQuantity, getConsommableCalculationDetail, getDisplayType } from '../../utils/consommableQuantity';
import {
  ArrowLeft,
  BookOpen,
  Users,
  Calculator,
  Save,
  ShieldCheck,
  Calendar,
  CalendarDays,
  Briefcase,
  User,
  Clock,
  XCircle,
  CheckCircle
} from 'lucide-react';
import './NewSessionPage.css';

const EditSessionPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { isAdmin, user } = useAuth();
  const getCurrency = () => user?.centres_details?.[0]?.devise || 'EUR';
  const toMoney = (value) => formatMoney(value, { locale: i18n.language, currency: getCurrency() });
  const toast = useToast();

  const [formations, setFormations] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [selectedFormation, setSelectedFormation] = useState('');
  const [selectedTrainer, setSelectedTrainer] = useState('');
  const [selectedInstructor, setSelectedInstructor] = useState('');
  const [studentCount, setStudentCount] = useState('');
  const [trainerCount, setTrainerCount] = useState('1');
  const [visitorCount, setVisitorCount] = useState('0');
  const [visitorDays, setVisitorDays] = useState('0');
  const [staffCount, setStaffCount] = useState('0');
  const [status, setStatus] = useState('EN_ATTENTE');
  const [comment, setComment] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [workdaysOnly, setWorkdaysOnly] = useState(true);
  const [consumeStock, setConsumeStock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [consommables, setConsommables] = useState([]);
  const navigate = useNavigate();
  const dateInputRef = React.useRef(null);
  const selectedFormationDetails = formations.find(f => f.id === selectedFormation);
  const filteredInstructors = useMemo(() => instructors.filter((instructor) => {
    if (!selectedFormationDetails?.centre) return true;
    return (instructor.centres || []).map(String).includes(String(selectedFormationDetails.centre));
  }), [instructors, selectedFormationDetails?.centre]);

  // Charger la session existante
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const session = await sessionService.getById(id);
        setSelectedFormation(session.formation);
        setSelectedTrainer(session.trainer ? String(session.trainer) : '');
        setSelectedInstructor(session.instructor ? String(session.instructor) : '');
        setStudentCount(session.student_count.toString());
        setTrainerCount((session.trainer_count || 1).toString());
        setVisitorCount(String(session.visitor_count || 0));
        setVisitorDays(String(session.visitor_days || 0));
        setStaffCount(String(session.staff_count || 0));
        setStatus(session.status || 'EN_ATTENTE');
        setComment(session.comment || '');
        setStartDate(session.start_date);
        setEndDate(session.end_date);
        setWorkdaysOnly(session.workdays_only);
        setConsumeStock(Boolean(session.consume_stock));

        // Load the training to get supply details.
        const formationsList = await formationService.getSessionOptions();
        const formation = formationsList.find(f => f.id === session.formation);

        if (formation && formation.consommables && session.budget_lines) {
          // Map training supplies with session quantities and comments.
          const consommablesData = formation.consommables.map(c => {
            const budgetLine = session.budget_lines.find(bl => bl.consommable_name === c.name);
            let customQty = null;
            if (budgetLine) {
              customQty = budgetLine.quantity;
            }
            return {
              id: c.id,
              name: c.name,
              type_consommable: c.type_consommable,
              quantity_fixe: c.quantity_fixe || 0,
              quantity_per_student: c.quantity_per_student || 0,
              quantity_per_trainer: c.quantity_per_trainer || 0,
              quantity_per_visitor: c.quantity_per_visitor || 0,
              quantity_per_staff: c.quantity_per_staff || 0,
              is_per_day: !!c.is_per_day,
              unit_price: c.unit_price,
              supplier_details: c.supplier_details,
              customQuantity: customQty,
              comment: budgetLine ? (budgetLine.comment || '') : ''
            };
          });
          setConsommables(consommablesData);
        }
      } catch (error) {
        console.error('Error fetching session:', error);
        toast.error(t('sessions.edit.errors.load'));
      }
    };
    fetchSession();
  }, [id, toast]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [formationsRes, trainersRes, instructorsRes] = await Promise.all([
          formationService.getSessionOptions(),
          userService.getTrainers(),
          sessionService.listInstructors()
        ]);
        setFormations(formationsRes);
        setResponsables(trainersRes.results || trainersRes || []);
        setInstructors(instructorsRes.results || instructorsRes || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedFormation && studentCount) {
      calculatePreview();
    }
  }, [selectedFormation, studentCount, trainerCount, visitorCount, visitorDays, staffCount, consommables, startDate, endDate]);

  useEffect(() => {
    if (!selectedInstructor) return;
    const stillAllowed = filteredInstructors.some((instructor) => String(instructor.id) === String(selectedInstructor));
    if (!stillAllowed) {
      setSelectedInstructor('');
    }
  }, [selectedFormation, selectedInstructor, filteredInstructors]);

  useEffect(() => {
    const formation = formations.find(f => f.id === selectedFormation);
    if (formation && formation.consommables && consommables.length === 0) {
      const initialConsommables = formation.consommables.map(c => ({
        id: c.id,
        name: c.name,
        type_consommable: c.type_consommable,
        quantity_fixe: c.quantity_fixe || 0,
        quantity_per_student: c.quantity_per_student || 0,
        quantity_per_trainer: c.quantity_per_trainer || 0,
        quantity_per_visitor: c.quantity_per_visitor || 0,
        quantity_per_staff: c.quantity_per_staff || 0,
        is_per_day: !!c.is_per_day,
        unit_price: c.unit_price,
        supplier_details: c.supplier_details,
        customQuantity: null,
        comment: ''
      }));
      setConsommables(initialConsommables);
    }
  }, [selectedFormation, formations]);

  const calculatePreview = () => {
    const formation = formations.find(f => f.id === selectedFormation);
    if (!formation || !studentCount) {
      setPreview(null);
      return;
    }

    const durationDays = formation.duration_days || 1;
    const totalsByCurrency = {};
    const lines = [];

    consommables.forEach(c => {
      const baseQuantity = calculateConsommableQuantity(c, {
        students: parseInt(studentCount || '0'),
        trainers: parseInt(trainerCount || '0'),
        visitors: parseInt(visitorCount || '0'),
        visitorDays: parseInt(visitorDays || '0'),
        staff: parseInt(staffCount || '0'),
        durationDays
      });
      const quantity = c.customQuantity !== null ? c.customQuantity : baseQuantity;
      const lineTotal = quantity * parseFloat(c.unit_price);

      // Add to totals by currency
      const currency = c.effective_devise || getCurrency();
      totalsByCurrency[currency] = (totalsByCurrency[currency] || 0) + lineTotal;

      lines.push({
        id: c.id,
        name: c.name,
        quantity,
        unit_price: c.unit_price,
        line_total: lineTotal,
        type: getDisplayType(c.type_consommable),
        supplier: c.supplier_details?.name,
        currency: c.effective_devise
      });
    });

    setPreview({ totalsByCurrency, lines });
  };

  const calculateEndDate = (start, durationDays, excludeWeekends) => {
    if (!start || !durationDays) return '';
    
    const startDate = new Date(start);
    let daysToAdd = parseInt(durationDays);
    let currentDate = new Date(startDate);
    
    if (excludeWeekends) {
      let addedDays = 0;
      while (addedDays < daysToAdd - 1) {
        currentDate.setDate(currentDate.getDate() + 1);
        const dayOfWeek = currentDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          addedDays++;
        }
      }
    } else {
      currentDate.setDate(currentDate.getDate() + daysToAdd - 1);
    }
    
    return currentDate.toISOString().split('T')[0];
  };

  useEffect(() => {
    const formation = formations.find(f => f.id === selectedFormation);
    if (formation && startDate) {
      const calculatedEndDate = calculateEndDate(startDate, formation.duration_days, workdaysOnly);
      setEndDate(calculatedEndDate);
    }
  }, [startDate, selectedFormation, formations, workdaysOnly]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFormation || !studentCount || !startDate) return;

    setSubmitting(true);
    try {
      // Prepare supply quantity overrides.
      const formation = formations.find(f => f.id === selectedFormation);
      const durationDays = formation?.duration_days || 1;

      const consommablesModifications = consommables.map(c => {

        const baseQuantity = calculateConsommableQuantity(c, {
          students: parseInt(studentCount || '0'),
          trainers: parseInt(trainerCount || '0'),
          visitors: parseInt(visitorCount || '0'),
          visitorDays: parseInt(visitorDays || '0'),
          staff: parseInt(staffCount || '0'),
          durationDays
        });
        const finalQuantity = c.customQuantity !== null ? c.customQuantity : baseQuantity;
        
        return {
          consommable_id: c.id,
          quantity: finalQuantity,
          comment: c.comment || ''
        };
      });

      const payload = {
        formation: selectedFormation,
        trainer: selectedTrainer,
        instructor: selectedInstructor || null,
        student_count: parseInt(studentCount || '0'),
        trainer_count: parseInt(trainerCount || '0'),
        visitor_count: parseInt(visitorCount || '0'),
        visitor_days: parseInt(visitorDays || '0'),
        staff_count: parseInt(staffCount || '0'),
        status: status,
        start_date: startDate,
        end_date: endDate,
        workdays_only: workdaysOnly,
        consume_stock: consumeStock,
        comment: comment,
        consommables_modifications: consommablesModifications
      };

      await sessionService.update(id, payload);
      toast.success(t('sessions.edit.updateSuccess'));
      navigate(`/sessions/${id}`);
    } catch (error) {
      console.error('Error updating session:', error);
      toast.error(t('sessions.edit.errors.update'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuantityChange = (consommableId, newQuantity) => {
    setConsommables(prev => prev.map(c => 
      c.id === consommableId 
        ? { ...c, customQuantity: newQuantity === '' ? null : parseInt(newQuantity) }
        : c
    ));
  };

  const handleConsommableCommentChange = (consommableId, newComment) => {
    setConsommables(prev => prev.map(c => 
      c.id === consommableId 
        ? { ...c, comment: newComment }
        : c
    ));
  };

  if (loading) {
    return <div className="page-loading">{t('common.loading')}</div>;
  }

  return (
    <div className="new-session-page">
      <div className="wizard-header">
        <Link to={`/sessions/${id}`} className="back-link">
          <ArrowLeft size={20} />
          {t('sessions.detail.back')}
        </Link>
        <div>
          <h1 className="wizard-title">{t('sessions.edit.title')}</h1>
          <p className="wizard-subtitle">{t('sessions.wizard.subtitle')}</p>
        </div>
      </div>

      <div className="new-session-grid">
        <div className="wizard-form-section">
          <form onSubmit={handleSubmit} className="form-card">
            <div className="form-header">
              <BookOpen size={24} className="form-icon" />
              <h2>{t('sessions.wizard.config')}</h2>
            </div>

            <div className="form-body">
              <div className="form-group">
                <label htmlFor="formation">
                  <BookOpen size={16} />
                  {t('formations.table.title')} *
                </label>
                <SearchableFormationSelect
                  formations={formations}
                  value={selectedFormation}
                  onChange={setSelectedFormation}
                  placeholder={t('sessions.edit.selectFormation')}
                  disabled
                />
                <p className="field-hint">{t('sessions.edit.errors.required')}</p>
              </div>

              <div className="form-group">
                <label htmlFor="trainer">
                  <User size={16} />
                  {t('sessions.edit.fields.trainer')} *
                </label>
                <select
                  id="trainer"
                  value={selectedTrainer}
                  onChange={(e) => setSelectedTrainer(e.target.value)}
                  required
                  className="form-input ui-input"
                >
                  <option value="">{t('sessions.edit.selectTrainer')}</option>
                  {responsables.map((resp) => (
                    <option key={resp.id} value={String(resp.id)}>
                      {resp.first_name} {resp.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="instructor">
                  <User size={16} />
                  {t('sessions.edit.fields.instructor')}
                </label>
                <select
                  id="instructor"
                  value={selectedInstructor}
                  onChange={(e) => setSelectedInstructor(e.target.value)}
                  className="form-input ui-input"
                >
                  <option value="">{t('sessions.edit.instructorPlaceholder')}</option>
                  {filteredInstructors.map((instructor) => (
                    <option key={instructor.id} value={String(instructor.id)}>
                      {instructor.full_name || `${instructor.first_name} ${instructor.last_name}`}
                    </option>
                  ))}
                </select>
                <p className="field-hint">{t('sessions.wizard.instructorHint')}</p>
              </div>

              <div className="form-group">
                <label htmlFor="studentCount">
                  <Users size={16} />
                  {t('sessions.edit.fields.participants')} *
                </label>
                <div className="input-with-icon">
                  <Users size={18} className="input-icon" />
                  <input
                    type="number"
                    id="studentCount"
                    value={studentCount}
                    onChange={(e) => setStudentCount(e.target.value)}
                    min="1"
                    required
                    className="form-input ui-input"
                    placeholder={t('sessions.wizard.participantsPlaceholder')}
                  />
                </div>
                <p className="field-hint">
                  {t('sessions.wizard.participantsHint')}
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="visitorCount">
                  <Users size={16} />
                  {t('sessions.edit.fields.visitors')}
                </label>
                <div className="input-with-icon">
                  <Users size={18} className="input-icon" />
                  <input
                    type="number"
                    id="visitorCount"
                    value={visitorCount}
                    onChange={(e) => setVisitorCount(e.target.value)}
                    min="0"
                    className="form-input ui-input"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="visitorDays">
                  <CalendarDays size={16} />
                  {t('sessions.edit.fields.visitorDays')}
                </label>
                <div className="input-with-icon">
                  <CalendarDays size={18} className="input-icon" />
                  <input
                    type="number"
                    id="visitorDays"
                    value={visitorDays}
                    onChange={(e) => setVisitorDays(e.target.value)}
                    min="0"
                    className="form-input ui-input"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="staffCount">
                  <Users size={16} />
                  {t('sessions.edit.fields.staffCount')}
                </label>
                <div className="input-with-icon">
                  <Users size={18} className="input-icon" />
                  <input
                    type="number"
                    id="staffCount"
                    value={staffCount}
                    onChange={(e) => setStaffCount(e.target.value)}
                    min="0"
                    className="form-input ui-input"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="trainerCount">
                  <Users size={16} />
                  {t('sessions.wizard.trainersLabel')}
                </label>
                <div className="input-with-icon">
                  <Users size={18} className="input-icon" />
                  <input
                    type="number"
                    id="trainerCount"
                    value={trainerCount}
                    onChange={(e) => setTrainerCount(e.target.value)}
                    min="1"
                    required
                    className="form-input ui-input"
                    placeholder={t('sessions.wizard.trainersPlaceholder')}
                  />
                </div>
                <p className="field-hint">
                  {t('sessions.wizard.trainersHint')}
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="startDate">
                  <Calendar size={16} />
                  {t('sessions.edit.fields.startDate')} *
                </label>
                <div className="input-with-icon">
                  <Calendar 
                    size={18} 
                    className="input-icon" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => dateInputRef.current?.showPicker()}
                  />
                  <input
                    type="date"
                    id="startDate"
                    ref={dateInputRef}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                    className="form-input ui-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <p className="field-hint" style={{ marginTop: '0', marginBottom: '0.5rem' }}>
                  {workdaysOnly 
                    ? t('sessions.wizard.workdaysHint')
                    : t('sessions.wizard.calendarHint')}
                </p>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={workdaysOnly}
                    onChange={(e) => setWorkdaysOnly(e.target.checked)}
                  />
                  <Briefcase size={16} />
                  <span>{t('sessions.edit.fields.workdaysOnly')}</span>
                </label>
              </div>

              <div className="form-group stock-consumption-option">
                <p className="field-hint" style={{ marginTop: '0', marginBottom: '0.5rem' }}>
                  {t('sessions.wizard.consumeStockHint')}
                </p>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={consumeStock}
                    onChange={(e) => setConsumeStock(e.target.checked)}
                    disabled={Boolean(status === 'VALIDEE')}
                  />
                  <ShieldCheck size={16} />
                  <span>{t('sessions.wizard.consumeStock')}</span>
                </label>
              </div>

              {selectedFormation && startDate && (
                <div className="form-group date-preview">
                  <label>
                    <CalendarDays size={16} />
                    {t('sessions.edit.fields.endDate')}
                  </label>
                  <div className="date-range-display">
                    <span className="start-date">{new Date(startDate).toLocaleDateString(i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'en' ? 'en-US' : 'fr-FR')}</span>
                    <span className="arrow">→</span>
                    <span className="end-date">{endDate ? new Date(endDate).toLocaleDateString(i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'en' ? 'en-US' : 'fr-FR') : '-'}</span>
                    <span className="duration-badge">
                      {t('common.daysCount', { count: formations.find(f => f.id === selectedFormation)?.duration_days })}
                    </span>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="status">
                  <CheckCircle size={16} />
                  {t('sessions.edit.fields.status')} *
                </label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  required
                  className="form-input ui-input"
                >
                  <option value="EN_ATTENTE">{t('status.session.EN_ATTENTE')}</option>
                  {isAdmin && <option value="VALIDEE">{t('status.session.VALIDEE')}</option>}
                  {isAdmin && <option value="REFUSEE">{t('status.session.REFUSEE')}</option>}
                  <option value="ANNULEE">{t('status.session.ANNULEE')}</option>
                </select>
                {!isAdmin && (
                  <p className="field-hint" style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                    {t('sessions.wizard.securityNotice')}
                  </p>
                )}
              </div>

              <div className="current-status" style={{ marginTop: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{t('sessions.edit.currentStatus')} :</span>
                <span style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.25rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: status === 'EN_ATTENTE' ? '#f59e0b' : status === 'VALIDEE' ? '#76b82a' : status === 'REFUSEE' ? '#ef4444' : '#6b7280'
                }}>
                  {status === 'EN_ATTENTE' && <Clock size={14} />}
                  {status === 'VALIDEE' && <CheckCircle size={14} />}
                  {status === 'REFUSEE' && <XCircle size={14} />}
                  {status === 'ANNULEE' && <XCircle size={14} />}
                  {t(`status.session.${status}`)}
                </span>
              </div>

            </div>

            <button
              type="submit"
              className="btn-submit-session"
              disabled={!selectedFormation || !selectedTrainer || !studentCount || submitting}
            >
              {submitting ? (
                <>
                  <span className="loader-small"></span>
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Save size={20} />
                  {t('common.save')}
                </>
              )}
            </button>
            <p className="security-notice">
              <ShieldCheck size={14} />
              {t('sessions.wizard.securityNotice')}
            </p>
          </form>
        </div>

        <div className="wizard-preview-section">
          {selectedFormation && studentCount ? (
            <div className="budget-invoice">
              <div className="invoice-header">
                <Calculator size={24} />
                <h2>{t('sessions.wizard.preview.title')}</h2>
              </div>

              <div className="invoice-program">
                <label>{t('sessions.wizard.preview.selectedFormation')}</label>
                <h3>{formations.find(f => f.id === selectedFormation)?.title}</h3>
              </div>

              {/* Commentaire global */}
              <div className="global-comment-section" style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem', color: '#0369a1' }}>
                  {t('sessions.wizard.preview.globalComment')}
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="form-input ui-input"
                  placeholder={t('sessions.wizard.preview.globalCommentPlaceholder')}
                  rows="3"
                  style={{ resize: 'vertical', width: '100%', fontSize: '0.875rem' }}
                />
              </div>

              {/* Liste des consommables modifiables */}
              <div className="invoice-items">
                {consommables.map((c) => {
                  const formation = formations.find(f => f.id === selectedFormation);
                  const durationDays = formation?.duration_days || 1;

                  const totalBaseQuantity = calculateConsommableQuantity(c, {
                    students: parseInt(studentCount || '0'),
                    trainers: parseInt(trainerCount || '0'),
                    visitors: parseInt(visitorCount || '0'),
                    visitorDays: parseInt(visitorDays || '0'),
                    staff: parseInt(staffCount || '0'),
                    durationDays
                  });
                  const calculationDetail = getConsommableCalculationDetail(c, {
                    students: parseInt(studentCount || '0'),
                    trainers: parseInt(trainerCount || '0'),
                    visitors: parseInt(visitorCount || '0'),
                    visitorDays: parseInt(visitorDays || '0'),
                    staff: parseInt(staffCount || '0'),
                    durationDays
                  }, t);
                  
                  const currentDisplayQuantity = c.customQuantity !== null ? c.customQuantity : totalBaseQuantity;
                  const currentTotalQuantity = currentDisplayQuantity;
                  const lineTotal = currentTotalQuantity * parseFloat(c.unit_price);

                  return (
                    <div key={c.id} className="consommable-card" style={{
                      padding: '1rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      background: '#fafafa'
                    }}>
                      {/* Supply header */}
                      <div className="consommable-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div>
                          <strong style={{ fontSize: '1rem' }}>{c.name}</strong>
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#6b7280', background: '#e5e7eb', padding: '0.125rem 0.5rem', borderRadius: '4px' }}>
                            {t(`consommables.types.${getDisplayType(c.type_consommable)}`)}
                          </span>
                          {c.is_per_day && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.70rem', color: '#0369a1', background: '#e0f2fe', padding: '0.125rem 0.5rem', borderRadius: '4px', fontWeight: 500 }}>
                              {t('consommables.perDay')}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {toMoney(c.unit_price)} / {t('consommables.unit')}
                        </div>
                      </div>

                      {/* Editable quantity */}
                      <div className="consommable-qty-row" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem', display: 'block' }}>
                            {c.type_consommable === 'FIXE' ? t('sessions.wizard.fixedQuantity') : t('sessions.wizard.quantityBase', { quantity: totalBaseQuantity })}
                          </label>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.35rem' }}>
                            {calculationDetail}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="number"
                              min="0"
                              value={currentDisplayQuantity}
                              onChange={(e) => handleQuantityChange(c.id, e.target.value)}
                              className="form-input ui-input"
                              style={{ width: '100%', maxWidth: '120px', padding: '0.5rem' }}
                            />
                          </div>
                        </div>
                        <div className="consommable-total" style={{ textAlign: 'right', minWidth: 0 }}>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>{t('sessions.wizard.total')} ({currentTotalQuantity} {t('consommables.totalUnits')})</div>
                          <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#059669' }}>
                            {toMoney(lineTotal)}
                          </div>
                        </div>
                      </div>

                      {/* Commentaire individuel */}
                      <div>
                        <label style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem', display: 'block' }}>
                          {t('sessions.wizard.preview.itemComment')}
                        </label>
                        <textarea
                          value={c.comment || ''}
                          onChange={(e) => handleConsommableCommentChange(c.id, e.target.value)}
                          className="form-input ui-input"
                          placeholder={t('sessions.wizard.preview.itemCommentPlaceholder')}
                          rows="2"
                          style={{ resize: 'vertical', width: '100%', fontSize: '0.75rem' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>


              {preview && (
                <div className="invoice-total">
                  {preview.totalsByCurrency && Object.keys(preview.totalsByCurrency).length > 0 ? (
                    Object.entries(preview.totalsByCurrency).map(([currency, amount]) => (
                      <div className="total-row" key={currency}>
                        <span className="total-label">{t('sessions.wizard.preview.totalHT')} ({currency})</span>
                        <span className="total-amount">{toMoney(amount, currency)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="total-row">
                      <span className="total-label">{t('sessions.wizard.preview.totalHT')}</span>
                      <span className="total-amount">-</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="preview-placeholder">
              <Calculator size={48} className="placeholder-icon" />
              <h3>{t('sessions.wizard.preview.title')}</h3>
              <p>{t('sessions.wizard.preview.globalCommentPlaceholder')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditSessionPage;
